import React, { useEffect, useState } from "react";
import { Role, getRolesByApplicationId } from "@/api/roles";
import RoleDetailList from "@/components/roles/RoleDetailList";
import AddButton from "@/ui-controls/AddButton";
import { Application, getApplication } from "@/api/applications";
import NewAppRolePanel from "@/components/roles/appRoles/NewAppRolePanel";
import SearchBox from "@/ui-controls/SearchBox";
import { IconButton } from "@fluentui/react";
import { useNavigate } from "react-router-dom";

interface Props {
  ApplicationId: number;
  ShowOnlyNewAppRolePanel: boolean;
  onCloseNewAppRolePanel?: () => any;
  onAddNewRole?: (role: Role) => any;
  onApplicationLoaded?: (name: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
}

const AppRoles = (props: Props) => {
  const navigate = useNavigate();
  const [appRoles, setAppRoles] = useState<Role[]>([]);
  const [newAppRolePanelOpenned, setNewAppRolePanelOpenned] = useState<boolean>(
    props.ShowOnlyNewAppRolePanel
  );
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );

  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    getAppRoles();
    getApplicationById();
  }, [props.ApplicationId]);

  const getAppRoles = () => {
    getRolesByApplicationId(props.ApplicationId)
      .then((result) => {
        setAppRoles(result);
        setFilteredRoles(result);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const getApplicationById = () => {
    getApplication(props.ApplicationId)
      .then((result) => {
        setApplication(result);
        if (props.onApplicationLoaded && result?.Name) {
          props.onApplicationLoaded(result.Name);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleError = (error: any) => {
    // Error will be handled by UI notification system when implemented
  };

  const handleRoleSearch = (searchValue: string) => {
    setSearchValue(searchValue);

    const filteredRoles = appRoles.filter(
      (role) =>
        role.Name.toLowerCase().includes(searchValue.toLowerCase()) ||
        (role.Description &&
          role.Description.toLowerCase().includes(searchValue.toLowerCase()))
    );

    setFilteredRoles(filteredRoles);
  };

  const handleRefresh = () => {
    setSearchValue("");
    setFilteredRoles([]);
    getAppRoles();
  };

  return (
    <>
      {!props.ShowOnlyNewAppRolePanel ? (
        <>
          <div className="page-header">
            <div className="breadcrumb-left-section">
              <IconButton
                iconProps={{ iconName: "Back" }}
                title="Back to Applications"
                onClick={() => navigate("/applications")}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: { fontSize: 18, color: "#0078d4" },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                }}
              />
              <div>
                {props.breadcrumbTitle && (
                  <h2 className="page-breadcrumb-title">
                    {props.breadcrumbTitle}
                  </h2>
                )}
                {props.breadcrumbSubtitle && (
                  <div style={{ color: "#605e5c", fontSize: "14px" }}>
                    {props.breadcrumbSubtitle}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <AddButton
                text="Role"
                onClick={() => setNewAppRolePanelOpenned(true)}
              />
              <SearchBox placeholder="Search" onSearch={handleRoleSearch} />
              <IconButton
                iconProps={{ iconName: "Refresh" }}
                title="Refresh"
                onClick={handleRefresh}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: { fontSize: 18, color: "#0078d4" },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                }}
              />
            </div>
          </div>
          <div>
            {application ? (
              <RoleDetailList
                Application={application}
                RoleList={searchValue === "" ? appRoles : filteredRoles}
                onDelete={(appRole) => {
                  setAppRoles((prev) =>
                    prev.filter((r) => r.Id !== appRole.Id)
                  );
                }}
              />
            ) : null}
          </div>
        </>
      ) : null}
      {application ? (
        <NewAppRolePanel
          application={application}
          isPanelOpenned={newAppRolePanelOpenned}
          onPanelClosed={() => {
            setNewAppRolePanelOpenned(false);
            if (props.onCloseNewAppRolePanel) props.onCloseNewAppRolePanel();
          }}
          onSave={(newRole) => {
            setAppRoles([...appRoles, newRole]);
            if (props.onAddNewRole) props.onAddNewRole(newRole);
          }}
        />
      ) : null}
    </>
  );
};

export default AppRoles;
